import { doc, getDoc, onSnapshot, serverTimestamp, setDoc, updateDoc, type Unsubscribe } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import type { UserProfile, UserProfileUpdate } from '../types/user';
import { updateProfile, type User } from 'firebase/auth';
import { downloadAndStoreProfilePhoto } from './storageService';


/** 
 * Returns true if the user's profile is complete. 
 * Accepts a UserProfile object directly.
 */
export async function isUserProfileComplete(profileOrId: UserProfile | string): Promise<boolean> {
  let profile;
  if (typeof profileOrId === 'string') {
    profile = (await getUserProfile(profileOrId))?.profile;
  } else {
    profile = profileOrId;
  }
  if (profile === null) {
    return false;
  }
  const requiredFields = ['displayName', 'email', 'phoneNumber', 'photoURL'];

  if (!profile) {
    return false;
  }

  for (const field of requiredFields) {
    if (profile[field as keyof typeof profile] === null) {
      console.debug(`Profile incomplete: missing field ${field}`);
      return false;
    }
  }

  return true;
}

/**
 * Get multiple user profiles by user IDs
 * Returns a map of userId -> UserProfile
 */
export async function getUserProfiles(userIds: string[]): Promise<Map<string, UserProfile>> {
  const profiles = new Map<string, UserProfile>();

  // Fetch all user profiles in parallel
  const promises = userIds.map(async (userId) => {
    const profile = await getUserProfile(userId);
    if (profile) {
      profiles.set(userId, profile);
    }
  });

  await Promise.all(promises);

  return profiles;
}

/**
 * Update the last login value for the userID in both users and publicUserProfiles collections
 */
export async function updateLastLogin(userId: string): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    const publicProfileRef = doc(db, 'publicUserProfiles', userId);

    const now = serverTimestamp();

    await Promise.all([
      updateDoc(userRef, { 'metadata.lastLoginAt': now }),
      updateDoc(publicProfileRef, { 'metadata.lastLoginAt': now }),
    ]);
  } catch (error) {
    console.error('Error updating last login:', error);
  }
}
// Update public user profile based on privacy settings
/**
 * Subscribe to user profile changes in real-time
 * Returns an unsubscribe function to clean up the listener
 */
export function subscribeToUserProfile(
  uid: string,
  onProfileUpdate: (profile: UserProfile | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const userRef = doc(db, 'users', uid);

  return onSnapshot(
    userRef,
    async (snapshot) => {
      if (!snapshot.exists()) {
        onProfileUpdate(null);
        return;
      }


      const data = snapshot.data();

      if (data.metadata?.profileCompleted !== true && await isUserProfileComplete(data.profile)) {
        // Update profileCompleted flag if profile is now complete
        updateUserProfile(uid, {
          metadata: {
            profileCompleted: true,
          },
        }).catch((error) => {
          console.error('Failed to update profile completion status:', error);
        });
      }

      const profile = firestoreToUserProfile(data, uid);
      onProfileUpdate(profile);
    },
    (error) => {
      console.error('Error subscribing to user profile:', error);
      if (onError) {
        onError(error);
      }
    }
  );
}// Update user profile
export async function updateUserProfile(
  uid: string,
  updates: UserProfileUpdate
): Promise<void> {
  const user = auth.currentUser;

  // Update Firebase Auth profile (only if user is authenticated and we have relevant fields)
  if (user && updates.profile) {
    const authUpdates: { displayName?: string | null; photoURL?: string | null; } = {};

    // Firebase Auth requires non-empty strings or null to remove the value
    // But photoURL specifically requires a valid URL string or must be omitted
    if (updates.profile.displayName !== undefined) {
      authUpdates.displayName = updates.profile.displayName || null;
    }

    if (updates.profile.photoURL !== undefined) {
      // Only include photoURL if it's a non-empty string (valid URL)
      // Don't pass null or empty string - just omit it
      if (updates.profile.photoURL && updates.profile.photoURL.trim() !== '') {
        authUpdates.photoURL = updates.profile.photoURL;
      }
    }

    // Only call updateProfile if we have fields to update
    if (Object.keys(authUpdates).length > 0) {
      await updateProfile(user, authUpdates);
    }
  }

  // Update Firestore profile - prepare updates for Firestore using dot notation for nested updates
  const firestoreUpdates: Record<string, unknown> = {
    'metadata.updatedAt': serverTimestamp(),
  };

  // Profile updates
  if (updates.profile) {
    if (updates.profile.name !== undefined) {
      firestoreUpdates['profile.name'] = updates.profile.name || null;
    }
    if (updates.profile.displayName !== undefined) {
      firestoreUpdates['profile.displayName'] = updates.profile.displayName || null;
    }
    if (updates.profile.email !== undefined) {
      firestoreUpdates['profile.email'] = updates.profile.email || null;
    }
    if (updates.profile.phoneNumber !== undefined) {
      firestoreUpdates['profile.phoneNumber'] = updates.profile.phoneNumber || null;
    }
    if (updates.profile.photoURL !== undefined) {
      firestoreUpdates['profile.photoURL'] = updates.profile.photoURL || null;
    }
    if (updates.profile.bio !== undefined) {
      firestoreUpdates['profile.bio'] = updates.profile.bio || null;
    }
    if (updates.profile.location !== undefined) {
      firestoreUpdates['profile.location'] = updates.profile.location || null;
    }
    if (updates.profile.dateOfBirth !== undefined) {
      firestoreUpdates['profile.dateOfBirth'] = updates.profile.dateOfBirth || null;
    }
    if (updates.profile.pronouns !== undefined) {
      firestoreUpdates['profile.pronouns'] = updates.profile.pronouns || null;
    }
  }

  // Preference updates
  if (updates.preferences) {
    if (updates.preferences.notifications) {
      if (updates.preferences.notifications.email !== undefined) {
        firestoreUpdates['preferences.notifications.email'] = updates.preferences.notifications.email;
      }
      if (updates.preferences.notifications.push !== undefined) {
        firestoreUpdates['preferences.notifications.push'] = updates.preferences.notifications.push;
      }
      if (updates.preferences.notifications.sms !== undefined) {
        firestoreUpdates['preferences.notifications.sms'] = updates.preferences.notifications.sms;
      }
      if (updates.preferences.notifications.smsOptIn !== undefined) {
        firestoreUpdates['preferences.notifications.smsOptIn'] = updates.preferences.notifications.smsOptIn;
      }
      if (updates.preferences.notifications.dailyDigest !== undefined) {
        firestoreUpdates['preferences.notifications.dailyDigest'] = updates.preferences.notifications.dailyDigest;
      }
      if (updates.preferences.notifications.campaignUpdates !== undefined) {
        firestoreUpdates['preferences.notifications.campaignUpdates'] = updates.preferences.notifications.campaignUpdates;
      }
      if (updates.preferences.notifications.teamActivity !== undefined) {
        firestoreUpdates['preferences.notifications.teamActivity'] = updates.preferences.notifications.teamActivity;
      }
    }

    if (updates.preferences.privacy) {
      if (updates.preferences.privacy.profilePublic !== undefined) {
        firestoreUpdates['preferences.privacy.profilePublic'] = updates.preferences.privacy.profilePublic;
      }
      if (updates.preferences.privacy.showOnLeaderboards !== undefined) {
        firestoreUpdates['preferences.privacy.showOnLeaderboards'] = updates.preferences.privacy.showOnLeaderboards;
      }
      if (updates.preferences.privacy.showLocation !== undefined) {
        firestoreUpdates['preferences.privacy.showLocation'] = updates.preferences.privacy.showLocation;
      }
      if (updates.preferences.privacy.allowMessaging !== undefined) {
        firestoreUpdates['preferences.privacy.allowMessaging'] = updates.preferences.privacy.allowMessaging;
      }
    }
  }

  // Metadata updates
  if (updates.metadata) {
    if (updates.metadata.lastLoginAt !== undefined) {
      firestoreUpdates['metadata.lastLoginAt'] = updates.metadata.lastLoginAt;
    }
    if (updates.metadata.accountStatus !== undefined) {
      firestoreUpdates['metadata.accountStatus'] = updates.metadata.accountStatus;
    }
    if (updates.metadata.emailVerified !== undefined) {
      firestoreUpdates['metadata.emailVerified'] = updates.metadata.emailVerified;
    }
    if (updates.metadata.twoFactorEnabled !== undefined) {
      firestoreUpdates['metadata.twoFactorEnabled'] = updates.metadata.twoFactorEnabled;
    }
    if (updates.metadata.profileCompleted !== undefined) {
      firestoreUpdates['metadata.profileCompleted'] = updates.metadata.profileCompleted;
    }
  }

  await updateDoc(doc(db, 'users', uid), firestoreUpdates);
}
// Get user profile from Firestore
export async function getUserProfile(uid: string): Promise<UserProfile | null> {
  const userDoc = await getDoc(doc(db, 'users', uid));

  if (!userDoc.exists()) {
    return null;
  }

  const data = userDoc.data();

  return firestoreToUserProfile(data, uid);
}
export async function createUserProfile(user: User, downloadOAuthPhoto: boolean = false, maxRetries: number = 3): Promise<void> {
  let photoURL = user.photoURL;

  // Handle profile photo
  if (user.photoURL && downloadOAuthPhoto) {
    // Download and store OAuth provider photo
    try {
      console.log('Downloading OAuth profile photo for new user...');
      photoURL = await downloadAndStoreProfilePhoto(user.uid, user.photoURL);
    } catch (error) {
      console.error('Failed to download OAuth photo, using original URL:', error);
    }
  }

  const userProfile = {
    profile: {
      displayName: user.displayName || null,
      email: user.email || null,
      phoneNumber: user.phoneNumber || null,
      photoURL: photoURL || null,
    },
    preferences: {
      notifications: {
        email: true,
        push: true,
        sms: false,
        dailyDigest: true,
        campaignUpdates: true,
        teamActivity: true,
      },
      privacy: {
        profilePublic: true,
        showOnLeaderboards: true,
        showLocation: false,
        allowMessaging: true,
      },
    },
    metadata: {
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
      accountStatus: 'active',
      emailVerified: user.emailVerified || false,
      twoFactorEnabled: false,
      profileCompleted: false,
    },
  };

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      await setDoc(doc(db, 'users', user.uid), userProfile);
      console.log(`User profile created successfully for ${user.uid}`);

      // Create public profile
      const publicProfile = {
        uid: user.uid,
        profile: {
          displayName: user.displayName || null,
          photoURL: photoURL || null,
        },
        metadata: {
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
          accountStatus: 'active',
          emailVerified: user.emailVerified || false,
          profileCompleted: false,
        },
      };

      await setDoc(doc(db, 'publicUserProfiles', user.uid), publicProfile);
      console.log(`Public profile created successfully for ${user.uid}`);

      return;
    } catch (error) {
      console.error(`Profile creation attempt ${attempt}/${maxRetries} failed:`, error);

      if (attempt === maxRetries) {
        // Final attempt failed - throw to trigger backup strategy
        throw new Error(`Failed to create user profile after ${maxRetries} attempts: ${error}`);
      }

      // Wait before retrying (exponential backoff)
      const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
}
// Ensure user profile exists - create if missing

export async function ensureUserProfileExists(user: User): Promise<UserProfile | null> {
  try {
    let profile = await getUserProfile(user.uid);

    if (!profile) {
      console.log(`Profile not found for user ${user.uid}, creating...`);
      await createUserProfile(user);
      profile = await getUserProfile(user.uid);

      if (!profile) {
        throw new Error('Profile creation succeeded but profile still not found');
      }
    }

    return profile;
  } catch (error) {
    console.error('Failed to ensure user profile exists:', error);
    return null;
  }
}

function firestoreToUserProfile(data: Record<string, unknown>, userID: string): UserProfile {
  const profile = data.profile as Record<string, unknown> | undefined;
  const preferences = data.preferences as Record<string, unknown> | undefined;
  const notifications = preferences?.notifications as Record<string, unknown> | undefined;
  const privacy = preferences?.privacy as Record<string, unknown> | undefined;
  const metadata = data.metadata as Record<string, unknown> | undefined;

  // Helper to safely convert Firestore Timestamp to Date
  const toDate = (value: unknown): Date => {
    if (!value) return new Date();
    if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
      return value.toDate() as Date;
    }
    if (value instanceof Date) return value;
    return new Date();
  };

  const toDateOrNull = (value: unknown): Date | null => {
    if (!value) return null;
    if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
      return value.toDate() as Date;
    }
    if (value instanceof Date) return value;
    return null;
  };

  const userProfile: UserProfile = {
    uid: userID,
    profile: {
      name: (profile?.name as string | null) || null,
      displayName: (profile?.displayName as string | null) || null,
      email: (profile?.email as string | null) || null,
      photoURL: (profile?.photoURL as string | null) || null,
      bio: (profile?.bio as string | null) || null,
      location: (profile?.location as string | null) || null,
      dateOfBirth: (profile?.dateOfBirth as string | null) || null,
      phoneNumber: (profile?.phoneNumber as string | null) || null,
      pronouns: (profile?.pronouns as string | null) || null,
    },
    preferences: {
      notifications: {
        email: (notifications?.email as boolean | undefined) ?? true,
        push: (notifications?.push as boolean | undefined) ?? true,
        sms: (notifications?.sms as boolean | undefined) ?? false,
        smsOptIn: (notifications?.smsOptIn as boolean | undefined) ?? false,
        dailyDigest: (notifications?.dailyDigest as boolean | undefined) ?? true,
        campaignUpdates: (notifications?.campaignUpdates as boolean | undefined) ?? true,
        teamActivity: (notifications?.teamActivity as boolean | undefined) ?? true,
      },
      privacy: {
        profilePublic: (privacy?.profilePublic as boolean | undefined) ?? true,
        showOnLeaderboards: (privacy?.showOnLeaderboards as boolean | undefined) ?? true,
        showLocation: (privacy?.showLocation as boolean | undefined) ?? false,
        allowMessaging: (privacy?.allowMessaging as boolean | undefined) ?? true,
      },
    },
    metadata: {
      createdAt: toDate(metadata?.createdAt),
      updatedAt: toDate(metadata?.updatedAt),
      accountStatus: (metadata?.accountStatus as 'active' | 'suspended' | 'deleted' | undefined) || 'active',
      emailVerified: (metadata?.emailVerified as boolean | undefined) ?? false,
      twoFactorEnabled: (metadata?.twoFactorEnabled as boolean | undefined) ?? false,
      profileCompleted: (metadata?.profileCompleted as boolean | undefined) ?? false,
    },
  };

  const lastLoginAt = toDateOrNull(metadata?.lastLoginAt);
  if (lastLoginAt) {
    userProfile.metadata.lastLoginAt = lastLoginAt;
  }

  return userProfile;
}

/**
 * Returns the public profile link for a user.
 * If a username is provided, it uses the /u/username format.
 * Otherwise, it uses the /u/uid format.
 * 
 * @param uid The user's unique ID
 * @param username The user's human-readable username (optional)
 * @returns The relative path to the user's profile
 */
export function getUserProfileLink(uid: string, username?: string | null): string {
  const identifier = username || uid;
  return `/u/${identifier}`;
}
